
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
  viewChild
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { LoggedInUserStore } from '@logged-in-user-store';
import { sanitizeHtml } from '@shared-utils';
import { Auth } from '@types-lib';
import { ENVIRONMENT, EnvironmentAppNames } from 'environments';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'auth-login',
  imports: [ReactiveFormsModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements AfterViewInit {
  loggedInUserStore = inject(LoggedInUserStore);
  router = inject(Router);
  sanitizer = inject(DomSanitizer);
  environment = inject(ENVIRONMENT);
  appName: EnvironmentAppNames = this.environment.constants.app_name
  errorHandlingMap = input<Auth.LoginErrorHandlingMap>();
  additionalLoginErrors = input<Auth.AdditionalLoginError[]>([]);
  isLoading = input<boolean>(false);
  disabled = input<boolean>(false);
  loginSuccess = output<Auth.LoginResult>();
  // set to true internally or forced from the outside like in case of a page redirection loading
  _isLoading = computed<boolean>(() => this.loggedInUserStore.isLoading() || this.isLoading());
  error = signal<string | SafeHtml>('');
  // hacky solution for Chrome automatically entering credentials,
  // but not entering input values until user interaction happens
  isAutofilled = signal(false);
  loginForm = new FormGroup({
    username: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
    password: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
  });
  @ViewChild('email', { static: false }) emailInput!: ElementRef<HTMLInputElement>;
  readonly passwordInput = viewChild.required<ElementRef<HTMLInputElement>>('password');

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.emailInput.nativeElement.matches(':autofill')) {
        this.isAutofilled.set(true);
      }
    }, 200);
  }

  async login() {
    this.error.update(() => '');
    this.loggedInUserStore.login(this.loginForm.getRawValue());

    await firstValueFrom(this.loggedInUserStore.hasFinishedLoadingLoginApi$);

    // if the login has no multifactor authentication and the login didn't error out, wait for the user to finish loading
    if (!this.loggedInUserStore.mfaToken() && !this.loggedInUserStore.loginResource.error()) {
      await this.waitForUserDetailLoading();
    }

    const err = (this.loggedInUserStore.areaCodesResource.error() ||
      this.loggedInUserStore.loadUserDetailsResource.error() ||
      this.loggedInUserStore.loginResource.error() ||
      this.additionalErrorOccurred() ||
      null) as HttpErrorResponse | null;

    if (err) {
      this.loginSuccess.emit({ successful: false, error: err, user: null });
      this.error.update(() => this.getErrorMessage(err));
    } else {
      this.loginSuccess.emit({ successful: true, user: this.loggedInUserStore.user() });
    }
  }

  private waitForUserDetailLoading = () => firstValueFrom(this.loggedInUserStore.hasFinishedLoadingUserDetailsApi$);

  getErrorMessage(error: HttpErrorResponse): SafeHtml {
    const errorStatus = error.status;
    const errorFunction =
      (this.errorHandlingMap() && (this.errorHandlingMap()?.get(errorStatus) || this.errorHandlingMap()?.get(-1)));
    const loginError = errorFunction && errorFunction(error);
    const errorMessage = loginError?.message || 'An error occurred';
    const sanitizedMessage = sanitizeHtml(errorMessage);
  
    return this.sanitizer.bypassSecurityTrustHtml(sanitizedMessage);
  }

  private additionalErrorOccurred = (): HttpErrorResponse | null =>
    this.additionalLoginErrors()?.reduce(
      (acc: HttpErrorResponse | null, errorFunction: Auth.AdditionalLoginError) => acc || errorFunction(),
      null,
    );
}
